export interface GradientRange {
  min: number;
  max: number;
  color: string;
}

export interface SparklineConfig {
  data: Array<Point>;
  gradientRanges?: Array<GradientRange>;
  maxVisibleX?: number;
  minVisibleX?: number;
  minVisibleY?: number;
  maxVisibleY?: number;
  lineWidth?: number;
}

import _ from "lodash";
import { Component, Prop, Vue } from "vue-property-decorator";
import { Point } from "./ChartUtils";

const DEFAULT_LINE_WIDTH = 2;
@Component
export default class Sparkline extends Vue {
  canvasRef!: HTMLCanvasElement;
  context2d!: CanvasRenderingContext2D;
  gradient!: CanvasGradient;

  @Prop({
    required: true,
    default: () => {
      return {
        data: []
      };
    }
  })
  config!: SparklineConfig;

  @Prop({
    required: false,
    default: () => {
      return 100;
    }
  })
  redrawOnResizeDelay!: number;

  created() {
    window.addEventListener("resize", this.onResize);
  }

  destroyed() {
    window.removeEventListener("resize", this.onResize);
  }
  onResize() {
    this.drawChartDebounced();
  }

  mounted() {
    this.drawChartDebounced();
  }
  drawChartDebounced = _.debounce(this.drawChart, this.redrawOnResizeDelay, {
    maxWait: 1000
  });
  drawChart() {
    const data = this.config.data;

    this.canvasRef = this.$refs.chart as HTMLCanvasElement;

    if (!this.canvasRef) {
      return;
    }

    this.canvasRef.style.width = "100%";
    this.canvasRef.style.height = "100%";

    const canvasHeight = this.canvasRef.height;
    const canvasWidth = this.canvasRef.width;
    // Don't do this, causes Safari to run very very slow
    // this.canvasRef.width = this.canvasRef.offsetWidth;
    // this.canvasRef.height = this.canvasRef.offsetHeight;

    const maxPointY = _.maxBy(data, "y")!.y;
    const maxVisibleY = _.get(this.config, "maxVisibleY", maxPointY);

    const minPointY = _.minBy(data, "y")!.y;
    const minVisibleY = _.get(this.config, "minVisibleY", minPointY);

    const maxPointX = _.maxBy(data, "x")!.x;
    const maxVisibleX = _.get(this.config, "maxVisibleX", maxPointX);

    const minPointX = _.minBy(data, "x")!.x;
    const minVisibleX = _.get(this.config, "minVisibleX", minPointX);

    const ctx = this.canvasRef.getContext("2d");
    if (!ctx) {
      return;
    }

    this.context2d = ctx;

    // Set canvas background color for debugging
    // this.context2d.fillStyle = "#80808030";
    // this.context2d.fillRect(0, 0, canvasWidth, canvasHeight);

    // prevent divide by 0
    const yDiff = maxVisibleY - minVisibleY;
    const yScale = yDiff === 0 ? canvasHeight : canvasHeight / yDiff;
    // prevent divide by 0
    const xDiff = maxVisibleX - minVisibleX;
    const xScale = xDiff === 0 ? canvasWidth : canvasWidth / xDiff;

    // shift 0,0 of canvas so the points can be directly plotted on screen without
    // having to translate each one onto the canvas
    this.context2d.translate(
      minVisibleX * xScale * -1,
      canvasHeight + minVisibleY * yScale
    );

    // fliping y (with * -1) to put zero in the bottom (default is top)
    this.context2d.scale(xScale, -1 * yScale);

    // Plot the data (not drawn until stroke() at the very end)
    this.context2d.beginPath();
    // Move to first point (don't draw line)
    this.context2d.moveTo(data[0].x, data[0].y);
    for (let i = 1; i < data.length; i++) {
      this.context2d.lineTo(data[i].x, data[i].y);
    }

    // Reset transform before stroking to get lines of equal width
    // that do not scale inversly with the data (large numbers = small lines)
    this.context2d.setTransform(1, 0, 0, 1, 0, 0);

    // The gradient needs to start at the lowest point and go to the highest point
    // to match the graph (starting at bottom and top of canvas will not cover lines
    // correctly when they don't also go the full height of the canvas)
    // Need to use the scale becase the trasform has been reset
    const top = (maxVisibleY - maxPointY) * yScale;
    const bottom = canvasHeight - (minPointY - minVisibleY) * yScale;
    const gradient = this.context2d.createLinearGradient(0, bottom, 0, top);

    // If gradientRanges isn't set, draw black line
    if (this.config.gradientRanges) {
      addColorStops(gradient, minPointY, maxPointY, this.config.gradientRanges);
      this.context2d.strokeStyle = gradient;
    } else {
      this.context2d.strokeStyle = "black";
    }

    this.context2d.lineWidth = this.config.lineWidth || DEFAULT_LINE_WIDTH;
    this.context2d.stroke();

    // For debugging with reference gradient on left side
    // this.context2d.fillStyle = gradient;
    // this.context2d.fillRect(
    //   // canvasWidth - 10,
    //   0,
    //   // minPointY * yScale,
    //   (maxVisibleY - maxPointY) * yScale,
    //   canvasWidth / 10,
    //   (maxPointY - minPointY) * yScale
    // );
  }
}

function addColorStops(
  gradient: CanvasGradient,
  min: number,
  max: number,
  gradientRanges: Array<GradientRange>
) {
  const range = max - min;

  const filtered = gradientRanges.filter(g => {
    const minWithin = g.min >= min && g.min <= max;
    const maxWithin = g.max >= min && g.max <= max;
    const covers = g.min < min && g.max > max;
    return minWithin || maxWithin || covers;
  });

  const filteredAndMapped = filtered.map(g => {
    // clamp, ensure all points are on the visible graph
    return {
      min: Math.max(g.min, min),
      max: Math.min(g.max, max),
      color: g.color
    };
  });
  const scaled = filteredAndMapped.map(g => {
    // Handle single line
    if (range === 0) {
      return {
        min: 0,
        max: 1,
        color: g.color
      };
    }
    // scale: y / range
    return {
      min: Math.max((g.min - min) / range, 0),
      max: Math.min((g.max - min) / range, 1.0),
      color: g.color
    };
  });
  scaled.forEach(g => {
    gradient.addColorStop(g.min, g.color);
    gradient.addColorStop(g.max, g.color);
  });
}
